import type { PublicPlan, AIGeneratedContent } from '@data/publicPlans';
import GanttChart from '../GanttChart';
import { usePlanTimelineTasks } from './usePlanTimelineTasks';

type CopyFn = (en: string, de: string, hr: string) => string;
type Language = 'en' | 'de' | 'hr';

export type PlanTimelineEditorProps = {
	plan: PublicPlan;
	content: AIGeneratedContent;
	onContentChange: (next: AIGeneratedContent) => void;
	copy: CopyFn;
	language: Language;
	isEditing: boolean;
	companyUserId?: number | null;
	className?: string;
};

export default function PlanTimelineEditor({
	plan,
	content,
	onContentChange,
	copy,
	language,
	isEditing,
	companyUserId,
	className = ''
}: PlanTimelineEditorProps) {
	const { timelineTasks, projectStartDate, companyCalendar, handleTaskUpdate, moveTask } = usePlanTimelineTasks(
		plan,
		content,
		onContentChange,
		language,
		{ loadCompanySchedule: true, companyUserId: companyUserId ?? null }
	);

	const orderedTasks = timelineTasks;

	if (orderedTasks.length === 0) {
		return (
			<p className={`text-muted small mb-0 ${className}`}>
				{copy(
					'Add implementation steps on the Project Plan tab to build a timeline.',
					'Fügen Sie auf dem Tab „Projektplan“ Umsetzungsschritte hinzu, um einen Zeitplan zu erstellen.',
					'Dodajte korake izvedbe na kartici Projektni plan kako biste izgradili vremenski plan.'
				)}
			</p>
		);
	}

	const hasPlanSteps = orderedTasks.some((t) => t.id?.startsWith('plan-step-'));

	const timelineHint = hasPlanSteps
		? isEditing
			? copy(
					'Timeline follows your implementation steps. Click bars to adjust duration; use arrows above to reorder.',
					'Der Zeitplan folgt Ihren Umsetzungsschritten. Klicken Sie auf Balken für die Dauer; Pfeile oben für die Reihenfolge.',
					'Vremenski plan slijedi korake izvedbe. Kliknite trake za trajanje; strelice gore za redoslijed.'
				)
			: copy(
					'Timeline follows your implementation steps.',
					'Der Zeitplan folgt Ihren Umsetzungsschritten.',
					'Vremenski plan slijedi korake izvedbe.'
				)
		: isEditing
			? copy(
					'Adjust task duration on the chart. Reorder steps above when linked to instructions.',
					'Passen Sie die Dauer im Diagramm an. Reihenfolge oben bei verknüpften Schritten.',
					'Prilagodite trajanje na grafikonu. Redoslijed gore kad su koraci povezani.'
				)
			: copy(
					'Rough sequence and duration preview.',
					'Grobe Vorschau auf Ablauf und Dauer.',
					'Grubi prikaz redoslijeda i trajanja.'
				);

	return (
		<div className={className}>
			{isEditing && orderedTasks.length > 0 && (
				<div className="mb-3">
					<h6 className="small text-muted mb-2">
						{copy('Step order & duration (working days)', 'Reihenfolge und Dauer (Arbeitstage)', 'Redoslijed i trajanje (radni dani)')}
					</h6>
					<ul className="list-unstyled mb-0 small">
						{orderedTasks.map((task, idx) => (
							<li key={task.id} className="d-flex flex-wrap align-items-center gap-2 mb-2 p-2 border rounded bg-light">
								<span className="fw-semibold flex-grow-1">{task.name}</span>
								<span className="text-muted">
									{task.duration ?? 1} {copy('days', 'Tage', 'dana')}
								</span>
								{orderedTasks.length > 1 && (
									<div className="btn-group btn-group-sm flex-shrink-0">
										<button
											type="button"
											className="btn btn-outline-secondary"
											title={copy('Move earlier', 'Nach oben', 'Pomakni ranije')}
											disabled={idx === 0}
											onClick={() => moveTask(idx, 'up')}
										>
											<i className="bi bi-arrow-up" aria-hidden />
										</button>
										<button
											type="button"
											className="btn btn-outline-secondary"
											title={copy('Move later', 'Nach unten', 'Pomakni kasnije')}
											disabled={idx === orderedTasks.length - 1}
											onClick={() => moveTask(idx, 'down')}
										>
											<i className="bi bi-arrow-down" aria-hidden />
										</button>
									</div>
								)}
							</li>
						))}
					</ul>
				</div>
			)}

			<div className="d-flex justify-content-between align-items-center mb-2">
				<h6 className="small mb-0">{copy('Work plan chart', 'Arbeitsplan-Diagramm', 'Grafikon plana rada')}</h6>
			</div>
			<p className="small text-muted mb-2">{timelineHint}</p>
			<GanttChart
				tasks={orderedTasks}
				gardeners={[]}
				editable={isEditing}
				preview={!isEditing}
				companyCalendar={companyCalendar ?? undefined}
				compactLegalBadge
				className="hide-status-legend"
				onTaskUpdate={isEditing ? handleTaskUpdate : undefined}
			/>
			{projectStartDate && (
				<p className="text-muted small mt-2 mb-0">
					{copy('Planned start:', 'Geplanter Start:', 'Planirani početak:')}{' '}
					<strong>{new Date(projectStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
				</p>
			)}
		</div>
	);
}
